import { useEffect, useContext, useState } from "react";
import queryString from 'query-string';
import { View, Button, Loader, CheckboxField, TextField } from '@aws-amplify/ui-react';
import { signUp, signIn, signOut, getCurrentUser } from '@aws-amplify/auth';
import { useLocation } from 'react-router-dom';
import logo from '../../Images/meshLogo.svg'
import UserContext from "../../services/userService";
import './Signup.css'

const ERROR_MESSAGES = {
  InvalidPasswordException: 'Password is not correct. Password must be at least 8 characters in length and include numbers, uppercase and lowercase letters.',
}

const SIGN_IN_FAILED_MESSAGE = "Your account was created, but we couldn't sign you in automatically — please sign in normally."

const normaliseEmail = (email) => (email || '').trim().toLowerCase()

// Retries at most once, so a session that keeps coming back can't loop.
const signInInvitedUser = async ({ emailAddress, password }, isRetry = false) => {
  try {
    await signIn({ username: emailAddress, password })
    return true
  } catch (e) {
    console.error(e)
    if (e.name !== 'UserAlreadyAuthenticatedException' || isRetry) {
      return false
    }
    try {
      await signOut()
    } catch (signOutError) {
      console.error(signOutError)
      return false
    }
    return signInInvitedUser({ emailAddress, password }, true)
  }
}

const AccountSwitchPrompt = ({ currentEmail, invitedEmail, onContinue, onCancel, switchError }) => (
  <>
    <View className="center">
      <h2>You're already signed in</h2>
    </View>
    <p>You're currently signed in as <b>{currentEmail}</b>. This invite is for <b>{invitedEmail}</b>.</p>
    <p>Continuing will sign you out of the current session.</p>
    {switchError && <p className="amplify-field__error-message">Couldn't sign you out — please try again.</p>}
    <Button isFullWidth variation="primary" onClick={onContinue}>Continue</Button>
    <Button isFullWidth onClick={onCancel}>Cancel</Button>
  </>
)

const AccountSwitchCancelled = ({ currentEmail, invitedEmail }) => (
  <>
    <View className="center">
      <h2>Invite not accepted</h2>
    </View>
    <p>You're still signed in as <b>{currentEmail}</b>. Open this invite link again when you're ready to switch to <b>{invitedEmail}</b>.</p>
  </>
)

// The actual SignupForm component.
const SignupForm = ({ businessName, signupUser, loading, globalError, globalErrorMessage, setGlobalError, signInFailed }) => {
  const [showWelcome, setShowWelcome] = useState(true)
  const [terms, setTerms] = useState(false)
  const [firstName, setFirstName] = useState('')
  const [lastName, setLastName] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')

  const [errors, setErrors] = useState({})

  // Function executed when the user clicks the "Save details" button.
  const submitForm = () => {
    const newErrors = []
    if (!firstName) {
      newErrors.push(['firstName', 'Please complete both name fields'])
    }
    if (!lastName) {
      newErrors.push(['lastName', 'Please complete both name fields'])
    }
    if (!password) {
      newErrors.push(['password', 'Please enter a password'])
    }
    if (!confirmPassword) {
      newErrors.push(['confirmPassword', 'Please confirm the password'])
    }
    if (password && confirmPassword && password !== confirmPassword) {
      newErrors.push(['password', 'Passwords must match'])
      newErrors.push(['confirmPassword', 'Passwords must match'])
    }
    if (!terms) {
      newErrors.push(['terms', 'You must agree to our terms and conditions to continue'])
    }
    if (!newErrors.length > 0) {
      signupUser({ firstName, lastName, password })
    }
    setErrors(Object.fromEntries(newErrors))
  }

  // What gets rendered when the account exists but the automatic sign-in didn't happen.
  // Deliberately not the "Retry" UI below: registration already succeeded, so re-running it would fail.
  if (signInFailed) {
    return (
      <>
        <View className="center">
          <h2>{SIGN_IN_FAILED_MESSAGE}</h2>
        </View>
        <Button isFullWidth variation="primary" onClick={() => { window.location.href = '/login' }}>Go to sign in</Button>
      </>
    )
  }

  // What gets rendered when there's an error.
  if (globalError) {
    const errorMessage = globalErrorMessage || 'Oops! There was an error'
    return (
      <>
        <View className="center">
          <h2>{errorMessage}</h2>
        </View>
        <p>Please try again or contact <a href="emailto:support@mesh.id">support@mesh.id</a> if you continue to experience issues.</p>
        <Button isFullWidth variation="primary" onClick={() => setGlobalError(false)}>Retry</Button>
      </>
    )
  }

  // What gets rendered when the form is loading.
  if (loading || !businessName) {
    return <View className="center"><Loader /></View>
  }

  // What gets rendered when the user is signing up. Displays a message, and when 
  // the user clicks the "Continue" button, set `showWelcome` to false and show the form.
  if (showWelcome) {
    return (
      <>
        <View className="center">
          <h2>Welcome to Mesh</h2>
        </View>
        <p>You've been invited to join the team at <b>{businessName}</b>.</p>
        <p>Before you start, we need a few details.</p>
        <Button isFullWidth variation="primary" onClick={() => setShowWelcome(false)}>Continue</Button>
      </>
    )
  }

  // Assuming none of the above conditions are met, we're showing the form.
  return (
    <>
      <h2>Personal Information</h2>
      <View>
        <TextField
          inputMode="text"
          placeholder="First name"
          label="First name"
          labelHidden
          errorMessage={errors.firstName}
          hasError={errors.firstName}
          name="firstName"
          onChange={(e) => setFirstName(e.currentTarget.value)}
          value={firstName}
          />
        <TextField
          inputMode="text"
          placeholder="Last name"
          label="Last name"
          errorMessage={errors.lastName}
          hasError={errors.lastName}
          labelHidden
          name="lastName"
          onChange={(e) => setLastName(e.currentTarget.value)}
          value={lastName}
          />
      </View>
      <h2>Security</h2>
      <View>
        <TextField
          inputMode="text"
          type="password"
          placeholder="Password"
          label="Password"
          errorMessage={errors.password}
          hasError={errors.password}
          labelHidden
          name="password"
          onChange={(e) => setPassword(e.currentTarget.value)}
          value={password}
          />
        <TextField
          inputMode="text"
          type="password"
          placeholder="Confirm Password"
          label="Confirm Password"
          errorMessage={errors.confirmPassword}
          hasError={errors.confirmPassword}
          labelHidden
          name="confirmPassword"
          onChange={(e) => setConfirmPassword(e.currentTarget.value)}
          value={confirmPassword}
          />
      </View>
      <CheckboxField className="terms-checkbox"
        name="terms"
        checked={terms}
        errorMessage={errors.terms}
        hasError={errors.terms}
        onChange={(e) => setTerms(e.target.checked)}
        label={<span>I agree to the Mesh <a href="https://www.mesh.id/terms-of-service" target="_blank">Terms & Conditions</a></span>}
      />
      <Button isFullWidth variation="primary" onClick={submitForm}>Save details</Button>
    </>
  )
}

const Signup = () => {
  const location = useLocation();
  const api = useContext(UserContext)
  const [invite, setInvite] = useState({})
  const [globalError, setGlobalError] = useState(false)
  const [globalErrorMessage, setGlobalErrorMessage] = useState('')
  const [loading, setLoading] = useState(false)
  const [signInFailed, setSignInFailed] = useState(false)
  const [sessionChecked, setSessionChecked] = useState(false)
  const [currentSessionEmail, setCurrentSessionEmail] = useState('')
  const [accountSwitch, setAccountSwitch] = useState('idle')
  const [switchError, setSwitchError] = useState(false)

  useEffect(() => {
    let isCurrent = true
    const checkExistingSession = async () => {
      try {
        const currentUser = await getCurrentUser()
        if (!isCurrent) {
          return
        }
        setCurrentSessionEmail(currentUser?.signInDetails?.loginId || currentUser?.username || '')
      } catch (e) {
        if (!isCurrent) {
          return
        }
        setCurrentSessionEmail('')
      }
      setSessionChecked(true)
    }
    checkExistingSession();
    return () => {
      isCurrent = false
    }
  }, [])

  useEffect(() => {
    const fetchInvite = async () => {
      try {
        const { inviteId } = queryString.parse(location.search);
        const results = await api.validateInvite({ inviteId })
        if (results.inviteStatus !== 'VALID') {
          throw new Error('Invalid inviteId')
        }
        setInvite(results)
      } catch (e) {
        console.error(e)
        setGlobalError(true)
        return
      }
    }
    fetchInvite();
  }, [location, api])

  // Performs the signup and signin.
  const signupUser = async ({ firstName, lastName, password }) => {
    setLoading(true)
    const { emailAddress, customerId, userId, inviteId } = invite
    const params = {
      username: emailAddress,
      password,
      // Amplify v6 now uses options
      options: {
        userAttributes: {
          email: emailAddress,
          'custom:customer_id': customerId,
          'custom:invite_id': inviteId,
          'custom:user_id': userId,
          'given_name': firstName,
          'family_name': lastName,
        },
      },
    }
    // TODO: How to handle errors

    // Using Amplify v6 signUp and signIn functions
    try {
      await signUp(params)
    } catch (e) {
      if (ERROR_MESSAGES[e.code]) {
        setGlobalErrorMessage(ERROR_MESSAGES[e.code])
      }
      else {
        setGlobalErrorMessage(e.message)
      }
      setLoading(false)
      console.error(e)
      console.error(e.message)
      setGlobalError(true)
      return
    }

    // The account is created and activated by the Cognito triggers at this point, so a failing
    // sign-in must never be reported as a failed registration.
    const signedIn = await signInInvitedUser({ emailAddress, password })
    if (!signedIn) {
      setLoading(false)
      setSignInFailed(true)
      return
    }

    // This Sign-up form should use the <Authenticator> component, but it uses a custom form
    // and standard button. Hence useAuthenticator doesn't update after sign-in. Until we can refactor
    // the sign-in page, we'll force a page reload instead, which WILL refresh the Auth state.
    window.location.href = '/';

  }

  const invitedEmail = invite.emailAddress || ''
  const isDifferentAccount = Boolean(currentSessionEmail) && Boolean(invitedEmail)
    && normaliseEmail(currentSessionEmail) !== normaliseEmail(invitedEmail)
  const needsAccountSwitch = sessionChecked && isDifferentAccount && accountSwitch === 'idle'

  // Only advance past the prompt once signOut actually succeeds — otherwise the browser
  // is still authenticated as the old user and signUp must not run yet.
  const confirmAccountSwitch = async () => {
    setSwitchError(false)
    try {
      await signOut()
    } catch (e) {
      console.error(e)
      setSwitchError(true)
      return
    }
    setCurrentSessionEmail('')
    setAccountSwitch('confirmed')
  }

  const renderSignupContent = () => {
    if (!globalError && accountSwitch === 'cancelled') {
      return <AccountSwitchCancelled currentEmail={currentSessionEmail} invitedEmail={invitedEmail} />
    }
    if (!globalError && needsAccountSwitch) {
      return (
        <AccountSwitchPrompt
          currentEmail={currentSessionEmail}
          invitedEmail={invitedEmail}
          onContinue={confirmAccountSwitch}
          onCancel={() => setAccountSwitch('cancelled')}
          switchError={switchError} />
      )
    }
    return (
      <SignupForm
        businessName={invite.customerName}
        signupUser={signupUser}
        loading={loading || !sessionChecked}
        globalError={globalError}
        globalErrorMessage={globalErrorMessage}
        setGlobalError={setGlobalError}
        signInFailed={signInFailed} />
    )
  }

  return (
    <View className="signup">
      <View className="signup-container">
        <View textAlign="center" padding={24}>
          <img src={logo} />
        </View>
        <View className="signup-info-box">
          <View className="signup-form">
            {renderSignupContent()}
          </View>
        </View>
        
      </View>
    </View>
  );
}
export default Signup
